import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    deleteDoc,
    updateDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    increment,
    runTransaction,
} from 'firebase/firestore';
import { db } from './config';
import { queueOrderEmail, sendLowStockAlertEmail } from './email';
import { FALLBACK_SHIRT_SIZE_PRICES } from './products';
import { getErrorMessage } from '../utils/errorUtils';

const LOW_STOCK_THRESHOLD = 10;

const IMPRINT_PRICES_RECTANGLE = {
    'Full Front':150,'Medium Front':120,'Center Chest':50,'Across Chest':80,
    'Left Chest':50,'Right Chest':50,'Left Vertical':120,'Right Vertical':120,
    'Bottom Left':50,'Bottom Right':50,'Full Back':150,'Medium Back':120,
    'Locker Center Patch':50,'Across Shoulder':80,'Left Sleeve':40,'Right Sleeve':40,
};
const IMPRINT_PRICES_CIRCLE = {
    'Full Front':120,'Medium Front':80,'Center Chest':50,'Across Chest':80,
    'Left Chest':40,'Right Chest':40,'Left Vertical':100,'Right Vertical':100,
    'Bottom Left':50,'Bottom Right':50,'Full Back':120,'Medium Back':80,
    'Locker Center Patch':50,'Back Center':80,'Left Sleeve':40,'Right Sleeve':40,
};

// Subcategories that use per-size pricing with NO color dimension
const NO_COLOR_SUBCATEGORIES = ['Polo Shirt', 'Jersey'];
const CLOTHING_SUBCATEGORIES = ['T-Shirt', 'Polo Shirt', 'Jersey'];
function isGenericProduct(item) {
    const sub = item?.subcategory;
    if (!sub) return false;
    return !CLOTHING_SUBCATEGORIES.includes(sub);
}

/**
 * Calculate the correct total for a single cart item.
 *
 * Handles four cases:
 *   0. Generic / non-clothing → flat quantity × price
 *   1. Polo Shirt / Jersey    → sum of (qty × sizePrices[size]) for each size
 *                               Fallback: item.price × totalQty if sizePrices missing
 *   2. T-Shirt                → (sizePrices[size] + colorSurcharge) × qty + imprint × totalQty
 *   3. Fallback               → same as T-Shirt using FALLBACK_SHIRT_SIZE_PRICES
 */
function calcItemTotal(item) {
    if (!item) return 0;
    
    const quantities = item.quantities || {};
    let totalQty   = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
    if (totalQty === 0) totalQty = Number(item.quantity) || 0;
    if (totalQty === 0) return 0;

    if (isGenericProduct(item)) {
        return totalQty * (Number(item.price) || 0);
    }

    // ── Polo Shirt / Jersey ───────────────────────────────────────────────────
    if (NO_COLOR_SUBCATEGORIES.includes(item.subcategory)) {
        const sizePrices    = item.sizePrices && typeof item.sizePrices === 'object'
            ? item.sizePrices
            : {};
        const hasSizePrices = Object.keys(sizePrices).length > 0 && Object.keys(quantities).length > 0;

        if (hasSizePrices) {
            return Object.entries(quantities).reduce(
                (s, [size, q]) => s + (q || 0) * (Number(sizePrices[size]) || Number(item.price) || 0),
                0
            );
        }

        // Fallback: flat base price × total qty
        console.warn(
            `calcItemTotal: item "${item.name || item.type}" has no sizePrices — ` +
            `falling back to item.price (${item.price}) × totalQty (${totalQty})`
        );
        return totalQty * (Number(item.price) || 0);
    }

    // ── T-Shirt ───────────────────────────────────────────────────────────────
    // FIX: Prefer sizePrices stored on the item itself (saved at add-to-cart time
    // by ProductDetails.jsx). Only fall back to FALLBACK_SHIRT_SIZE_PRICES when
    // the item pre-dates the fix and has no sizePrices saved.
    const sizePrices = (item.sizePrices && Object.keys(item.sizePrices).length > 0)
        ? item.sizePrices
        : (FALLBACK_SHIRT_SIZE_PRICES[item.type] || {});

    const colorSurcharge = Number(item.colorSurcharge || 0);

    let shirtSubtotal = 0;
    if (Object.keys(quantities).length > 0) {
        shirtSubtotal = Object.entries(quantities).reduce(
            (s, [size, q]) => s + (q || 0) * ((Number(sizePrices[size]) || Number(item.price) || 0) + colorSurcharge),
            0
        );
    } else {
        shirtSubtotal = totalQty * ((Number(item.price) || 0) + colorSurcharge);
    }

    const shape  = (item.designShape || '').toLowerCase() === 'circle' ? 'circle' : 'rectangle';
    const table  = shape === 'circle' ? IMPRINT_PRICES_CIRCLE : IMPRINT_PRICES_RECTANGLE;
    const locs   = (item.printLocation || '').split(',').map(s => s.trim()).filter(Boolean);
    const prices = locs.map(l => table[l] || 0).sort((a, b) => b - a);
    const imprintPerPc = prices.reduce((s, p, i) => s + (i === 0 ? p : Math.round(p * 0.5)), 0);

    return shirtSubtotal + imprintPerPc * totalQty;
}

// ─── Order ID generator ───────────────────────────────────────────────────────
async function generateOrderId() {
    const counterRef = doc(db, 'counters', 'orderCounter');
    let newCount;

    await runTransaction(db, async (tx) => {
        const counterSnap = await tx.get(counterRef);
        if (!counterSnap.exists()) {
            newCount = 1;
            tx.set(counterRef, { value: 1 });
        } else {
            newCount = (counterSnap.data().value || 0) + 1;
            tx.update(counterRef, { value: newCount });
        }
    });

    const today    = new Date();
    const datePart = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
    const seqPart  = String(newCount).padStart(5, '0');
    return `JG-${datePart}-${seqPart}`;
}

// ─── Status constants ─────────────────────────────────────────────────────────
export const ORDER_STATUS = {
    PENDING_APPROVAL:           'pending approval',
    APPROVED:                   'approved',
    AWAITING_PAYMENT:           'awaiting payment confirmation',
    IN_PRODUCTION:              'in production',
    TO_BE_DELIVERED:            'to be delivered',
    DELIVERED:                  'delivered',
    CANCELLED:                  'cancelled',
};

export const APPROVAL_EXPIRY_MS = 72 * 60 * 60 * 1000;

// ─── Email queue writer ───────────────────────────────────────────────────────
async function queueEmail({ to, customerName, subject, templateId, orderId, status, orderTotal, extra = {} }) {
    if (!to) return;
    try {
        await queueOrderEmail({ to, customerName, subject, templateId, orderId, status, orderTotal, extra });
    } catch (err) {
        console.error('Failed to queue email:', err);
    }
}

// ─── Firestore notification writer ───────────────────────────────────────────
export async function writeNotification({ userId, orderId, title, message, status, orderTotal }) {
    if (!userId) return;
    try {
        await addDoc(collection(db, 'notifications', userId, 'items'), {
            orderId, title, message, status, orderTotal,
            read: false, createdAt: serverTimestamp(),
        });
    } catch (err) {
        console.error('Failed to write notification:', err);
    }
}

// ─── Status history entry ─────────────────────────────────────────────────────
function makeStatusEntry(status, note = '') {
    return { status, note, timestamp: new Date().toISOString() };
}

function buildActorLabel(performedBy = null) {
    if (!performedBy) return '';
    return String(performedBy.name || performedBy.adminName || performedBy.email || '').trim();
}

function getPaymentMethodLabel(paymentMethod = '') {
    return String(paymentMethod || '').trim() || 'payment';
}

// ─── createOrder ─────────────────────────────────────────────────────────────
export async function createOrder(userId, customerName, cartItems, note = '', address = null, customerEmail = '') {
    if (!userId)            return { orderId: null, firestoreId: null, error: 'No user ID provided.' };
    if (!customerName)      return { orderId: null, firestoreId: null, error: 'No customer name provided.' };
    if (!cartItems?.length) return { orderId: null, firestoreId: null, error: 'No cart items to order.' };

    try {
        const orderId     = await generateOrderId();
        const totalAmount = cartItems.reduce((sum, item) => sum + calcItemTotal(item), 0);
        const itemsCount  = cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const currentDate = new Date().toISOString().split('T')[0];

        const orderData = {
            orderId, userId, customerName,
            customerEmail: customerEmail || '',
            items: itemsCount,
            total: totalAmount,
            date:  currentDate,
            status: ORDER_STATUS.PENDING_APPROVAL,

            deliveryAddress: address ? {
                houseNo:   address.houseNo   || '',
                barangay:  address.barangay  || '',
                city:      address.city      || '',
                province:  address.province  || '',
                zipCode:   address.zipCode   || '',
                landmark:  address.landmark  || '',
            } : null,

            orderItems: cartItems.map(item => ({
                id:              item.id              || '',
                name:            item.name            || '',
                image:           item.image           || '',
                imageUrl:        item.imageUrl        || '',
                type:            item.type            || '',
                subcategory:     item.subcategory     || '',
                category:        item.category        || '',
                selectedAttributes: item.selectedAttributes && typeof item.selectedAttributes === 'object'
                    ? item.selectedAttributes : {},
                color:           item.color           || '',
                designShape:     item.designShape     || '',
                printLocation:   item.printLocation   || '',
                quantities:      item.quantities      || {},
                quantity:        item.quantity        || 0,
                sizeBreakdown:   item.sizeBreakdown   || '',
                note:            item.note            || '',
                teamName:        item.teamName        || '',
                surname:         item.surname         || '',
                number:          item.number          || '',
                // Persist pricing fields needed for correct total recalculation
                price:           item.price           || 0,
                sizePrices:      (item.sizePrices && typeof item.sizePrices === 'object')
                    ? item.sizePrices : {},
                colorSurcharge:  Number(item.colorSurcharge || 0),
                designImages:    (item.designImages && typeof item.designImages === 'object')
                    ? item.designImages : {},
                designEmailSent: Boolean(item.designEmailSent),
                designEmailSentAt: item.designEmailSentAt || null,
                storeEmail:      item.storeEmail || '',
                itemTotal:       calcItemTotal(item),
            })),

            note: note || '',
            paymentMethod:    '',
            gcashReference:   '',
            gcashScreenshot:  '',
            paymentConfirmed: false,
            expiresAt:        null,

            statusHistory: [
                makeStatusEntry(ORDER_STATUS.PENDING_APPROVAL, 'Order submitted by customer')
            ],

            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };

        const docRef = await addDoc(collection(db, 'orders'), orderData);

        await queueEmail({
            to: customerEmail, customerName,
            subject:    `Order Received — ${orderId}`,
            templateId: 'order-received',
            orderId, status: ORDER_STATUS.PENDING_APPROVAL, orderTotal: totalAmount,
        });

        return { orderId, firestoreId: docRef.id, error: null };
    } catch (err) {
        console.error('Error creating order:', err);
        let msg = getErrorMessage(err, 'Failed to create order. Please try again.');
        if (err.code === 'permission-denied') msg = 'Permission denied. Check Firestore security rules.';
        if (err.code === 'unavailable')       msg = 'Firebase is unavailable. Please try again.';
        if (err.code === 'unauthenticated')   msg = 'You must be logged in to create an order.';
        return { orderId: null, firestoreId: null, error: msg };
    }
}

// ─── getAllOrders ─────────────────────────────────────────────────────────────
export async function getAllOrders() {
    try {
        const q        = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const orders   = snapshot.docs.map(d => ({ firestoreId: d.id, ...d.data() }));
        return { orders, error: null };
    } catch (err) {
        console.error('Error fetching orders:', err);
        return { orders: [], error: getErrorMessage(err, 'Failed to fetch orders. Please try again.') };
    }
}

// ─── getUserOrders ────────────────────────────────────────────────────────────
export async function getUserOrders(userId) {
    if (!userId) return { orders: [], error: 'No user ID provided.' };
    try {
        const q        = query(collection(db, 'orders'), where('userId', '==', userId), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const orders   = snapshot.docs.map(d => ({ firestoreId: d.id, ...d.data() }));
        return { orders, error: null };
    } catch (err) {
        console.error('Error fetching user orders:', err);
        return { orders: [], error: getErrorMessage(err, 'Failed to fetch your orders. Please try again.') };
    }
}

// ─── getOrderByFirestoreId ────────────────────────────────────────────────────
export async function getOrderByFirestoreId(firestoreId) {
    if (!firestoreId) return { order: null, error: 'No ID provided.' };
    try {
        const snap = await getDoc(doc(db, 'orders', firestoreId));
        if (!snap.exists()) return { order: null, error: 'Order not found.' };
        return { order: { firestoreId: snap.id, ...snap.data() }, error: null };
    } catch (err) {
        return { order: null, error: getErrorMessage(err, 'Failed to fetch order. Please try again.') };
    }
}

// ─── SIZE KEY → Inventory display name ───────────────────────────────────────
const SIZE_KEY_TO_DISPLAY = {
    xxs:'XXS', xs:'XS', small:'Small', medium:'Medium', large:'Large',
    xl:'XL', xxl:'2XL', xxxl:'3XL', xxxxl:'4XL', xxxxxl:'5XL', xxxxxxl:'6XL',
    XXS:'XXS', XS:'XS', Small:'Small', Medium:'Medium', Large:'Large',
    XL:'XL', '2XL':'2XL', '3XL':'3XL', '4XL':'4XL', '5XL':'5XL', '6XL':'6XL',
};

// ─── deductInventoryForOrder ──────────────────────────────────────────────────
/**
 * Deducts stock from Firestore products when an order moves to IN_PRODUCTION.
 *
 * Handles:
 *   - Polo/Jersey (no-color): stock stored under inventory['_nocolor']
 *   - T-Shirt (color): stock stored under inventory[colorName]
 */
async function deductInventoryForOrder(orderItems, orderId = null) {
    if (!orderItems || orderItems.length === 0) return;

    for (const item of orderItems) {
        try {
            const productName = item.type || '';
            if (!productName) {
                console.warn('deductInventory: item has no type field, skipping:', item);
                continue;
            }

            const productQuery = query(collection(db, 'products'), where('name', '==', productName));
            const productSnap  = await getDocs(productQuery);
            if (productSnap.empty) {
                console.warn(`deductInventory: no product found with name "${productName}"`);
                continue;
            }

            const productDoc = productSnap.docs[0];
            const productData = productDoc.data();
            const inventory  = productData.inventory;
            const isNoColor  = NO_COLOR_SUBCATEGORIES.includes(item.subcategory);

            const noColorStock = isNoColor
                ? (inventory?.['_nocolor'] || productData.stockBySize || null)
                : null;

            if (isNoColor && !noColorStock) {
                console.warn(`deductInventory: product "${productName}" has no '_nocolor' or stockBySize inventory — skipping`);
                continue;
            }

            if (!isNoColor && (!inventory || typeof inventory !== 'object' || Object.keys(inventory).length === 0)) {
                console.warn(`deductInventory: product "${productName}" has no inventory data — skipping`);
                continue;
            }

            const quantities = item.quantities || {};
            let colorKey;

            if (isNoColor) {
                colorKey = '_nocolor';
            } else {
                const rawColor      = (item.color || '').trim();
                const rawColorLower = rawColor.toLowerCase();
                colorKey = Object.keys(inventory).find(k => k.toLowerCase() === rawColorLower);

                if (!colorKey) {
                    console.warn(
                        `deductInventory: color "${rawColor}" not found in inventory for "${productName}". ` +
                        `Available: ${Object.keys(inventory).join(', ')}`
                    );
                    continue;
                }
            }

            // ── Subtract each size quantity ───────────────────────────────────
            const currentColorStock = isNoColor ? noColorStock : inventory[colorKey];
            const updatedColorStock = { ...currentColorStock };
            let anyDeducted = false;

            for (const [sizeKey, qty] of Object.entries(quantities)) {
                if (!qty || qty <= 0) continue;

                const displaySize = SIZE_KEY_TO_DISPLAY[sizeKey];
                if (!displaySize) {
                    console.warn(`deductInventory: unknown size key "${sizeKey}" — add it to SIZE_KEY_TO_DISPLAY`);
                    continue;
                }

                if (updatedColorStock[displaySize] !== undefined) {
                    updatedColorStock[displaySize] = Math.max(0, (updatedColorStock[displaySize] || 0) - qty);
                    anyDeducted = true;
                } else {
                    const displaySizeLower = displaySize.toLowerCase();
                    const actualSizeKey    = Object.keys(updatedColorStock).find(
                        k => k.toLowerCase() === displaySizeLower
                    );
                    if (actualSizeKey) {
                        updatedColorStock[actualSizeKey] = Math.max(0, (updatedColorStock[actualSizeKey] || 0) - qty);
                        anyDeducted = true;
                    } else {
                        console.warn(
                            `deductInventory: size "${sizeKey}" (→ "${displaySize}") not found in ` +
                            `inventory["${colorKey}"] for "${productName}". ` +
                            `Available: ${Object.keys(updatedColorStock).join(', ')}`
                        );
                    }
                }
            }

            if (!anyDeducted) {
                console.warn(`deductInventory: no quantities deducted for "${productName}" / "${colorKey}"`);
                continue;
            }

            // ── Rebuild inventory and recalculate total ───────────────────────
            let newInventory;
            let newStockBySize;

            if (isNoColor) {
                newStockBySize = updatedColorStock;
                newInventory   = { ...(inventory || {}), ['_nocolor']: updatedColorStock };
            } else {
                newInventory   = { ...inventory, [colorKey]: updatedColorStock };
            }

            const newStockQuantity = Object.values(newInventory).reduce(
                (t, colorStock) =>
                    t + Object.values(colorStock).reduce((s, v) => s + (v || 0), 0),
                0
            );

            const updatePayload = {
                inventory:     newInventory,
                stockQuantity: newStockQuantity,
                updatedAt:     serverTimestamp(),
            };

            if (isNoColor) {
                updatePayload.stockBySize = newStockBySize;
            }

            await updateDoc(doc(db, 'products', productDoc.id), updatePayload);

            console.log(
                `deductInventory: ✓ "${productName}" / "${colorKey}" — new stockQuantity: ${newStockQuantity}`
            );

            // ── Low stock alert after deduction ───────────────────────────────
            const lowStockSizes = Object.entries(updatedColorStock)
                .filter(([, qty]) => qty <= LOW_STOCK_THRESHOLD)
                .map(([size, qty]) => ({ size, stock: qty }));

            if (lowStockSizes.length > 0) {
                const subcategory = productDoc.data().subcategory || '';
                sendLowStockAlertEmail({
                    productName,
                    subcategory,
                    lowStockSizes,
                    trigger:  'order',
                    orderId,
                }).catch(err => console.error('deductInventory: low stock alert failed:', err));
            }

        } catch (itemErr) {
            console.error(`deductInventory: failed for item "${item.type || item.name}":`, itemErr);
        }
    }
}

// ─── updateOrderStatus ────────────────────────────────────────────────────────
export async function updateOrderStatus(firestoreId, newStatus, opts = {}) {
    if (!firestoreId || !newStatus) return { success: false, error: 'Missing order ID or status.' };

    const {
        note               = '',
        userId             = '',
        customerName       = '',
        customerEmail      = '',
        orderId            = '',
        orderTotal         = 0,
        cancellationReason = '',
        estimatedDeliveryDate = '',
        paymentMethod      = '',
        performedBy        = null,
    } = opts;

    try {
        const orderRef = doc(db, 'orders', firestoreId);
        const updates  = { status: newStatus, updatedAt: serverTimestamp() };

        if (newStatus === ORDER_STATUS.APPROVED) {
            updates.expiresAt = new Date(Date.now() + APPROVAL_EXPIRY_MS).toISOString();
            const actorLabel = buildActorLabel(performedBy);
            if (actorLabel) {
                updates.approvedBy = {
                    uid:   performedBy?.uid   || '',
                    email: performedBy?.email || '',
                    role:  performedBy?.role  || '',
                    name:  actorLabel,
                };
                updates.approvedAt = new Date().toISOString();
            }
        }

        if ([ORDER_STATUS.IN_PRODUCTION, ORDER_STATUS.DELIVERED, ORDER_STATUS.TO_BE_DELIVERED].includes(newStatus)) {
            updates.expiresAt = null;
        }

        if (newStatus === ORDER_STATUS.CANCELLED && cancellationReason) {
            updates.cancellationReason = cancellationReason;
        }

        if (newStatus === ORDER_STATUS.TO_BE_DELIVERED && estimatedDeliveryDate) {
            updates.estimatedDeliveryDate = estimatedDeliveryDate;
        }

        const snap     = await getDoc(orderRef);
        const existing = snap.exists() ? (snap.data().statusHistory || []) : [];
        const resolvedPaymentMethod = paymentMethod || (snap.exists() ? (snap.data().paymentMethod || '') : '');
        const paymentMethodLabel = getPaymentMethodLabel(resolvedPaymentMethod);
        updates.statusHistory = [...existing, makeStatusEntry(newStatus, note || cancellationReason)];

        if (newStatus === ORDER_STATUS.IN_PRODUCTION) {
            const orderItems = snap.exists() ? (snap.data().orderItems || []) : [];
            await deductInventoryForOrder(orderItems, orderId);
        }

        await updateDoc(orderRef, updates);

        // ── In-app notification ───────────────────────────────────────────────
        const notifMessages = {
            [ORDER_STATUS.APPROVED]:         `Your order ${orderId} has been approved! You have 72 hours to check out.`,
            [ORDER_STATUS.AWAITING_PAYMENT]: `We received your ${paymentMethodLabel} payment for order ${orderId}. Awaiting confirmation.`,
            [ORDER_STATUS.IN_PRODUCTION]:    `Great news! Order ${orderId} is now in production.`,
            [ORDER_STATUS.TO_BE_DELIVERED]:  `Order ${orderId} is ready for pickup / delivery!`,
            [ORDER_STATUS.DELIVERED]:        `Order ${orderId} has been delivered. Thank you!`,
            [ORDER_STATUS.CANCELLED]: cancellationReason?.includes('Approval window expired')
                ? `Order ${orderId} was automatically cancelled — customer did not check out in time.`
                : `Order ${orderId} has been cancelled. ${cancellationReason ? 'Reason: ' + cancellationReason : ''}`,
        };

        const notifTitles = {
            [ORDER_STATUS.APPROVED]:         'Order Approved',
            [ORDER_STATUS.AWAITING_PAYMENT]: 'Payment Received — Pending Confirmation',
            [ORDER_STATUS.IN_PRODUCTION]:    'Order In Production',
            [ORDER_STATUS.TO_BE_DELIVERED]:  'Order Ready for Pickup',
            [ORDER_STATUS.DELIVERED]:        'Order Delivered',
            [ORDER_STATUS.CANCELLED]:        'Order Cancelled',
        };

        // ── Delete "Order Approved" notification when order moves past checkout ────
        if (
            newStatus === ORDER_STATUS.AWAITING_PAYMENT ||
            newStatus === ORDER_STATUS.IN_PRODUCTION
        ) {
            if (userId) {
                try {
                    const approvedNotifsQ = query(
                        collection(db, 'notifications', userId, 'items'),
                        where('status', '==', ORDER_STATUS.APPROVED),
                        where('orderId', '==', orderId)
                    );
                    const approvedSnap = await getDocs(approvedNotifsQ);
                    await Promise.all(approvedSnap.docs.map(d => deleteDoc(d.ref)));
                } catch (err) {
                    console.warn('Could not delete Order Approved notification:', err);
                }
            }
        }

        if (notifMessages[newStatus]) {
            await writeNotification({
                userId, orderId,
                title:      notifTitles[newStatus]   || newStatus,
                message:    notifMessages[newStatus] || '',
                status:     newStatus,
                orderTotal,
            });
        }

        // ── Email queue ───────────────────────────────────────────────────────
        const emailTemplates = {
            [ORDER_STATUS.APPROVED]:        { templateId:'order-approved',      subject:`Order ${orderId} Approved — Proceed to Checkout` },
            [ORDER_STATUS.IN_PRODUCTION]:   { templateId:'order-in-production', subject:`Order ${orderId} is Now In Production` },
            [ORDER_STATUS.TO_BE_DELIVERED]: { templateId:'order-ready-pickup',  subject:`Order ${orderId} is Ready for Pickup!` },
            [ORDER_STATUS.DELIVERED]:       { templateId:'order-delivered',     subject:`Order ${orderId} Delivered — Thank You!` },
            [ORDER_STATUS.CANCELLED]:       { templateId:'order-cancelled',     subject:`Order ${orderId} Has Been Cancelled` },
        };

        if (emailTemplates[newStatus]) {
            const { templateId, subject } = emailTemplates[newStatus];
            const emailExtra = {};
            if (cancellationReason) emailExtra.cancellationReason = cancellationReason;
            if (estimatedDeliveryDate && newStatus === ORDER_STATUS.TO_BE_DELIVERED) emailExtra.estimatedDeliveryDate = estimatedDeliveryDate;

            await queueEmail({
                to: customerEmail, customerName, subject, templateId,
                orderId, status: newStatus, orderTotal, extra: emailExtra,
            });
        }

        return { success: true, error: null };
    } catch (err) {
        console.error('Error updating order status:', err);
        return { success: false, error: getErrorMessage(err, 'Failed to update order status. Please try again.') };
    }
}

// ─── approveOrder ────────────────────────────────────────────────────────────
export async function approveOrder(firestoreId, opts = {}) {
    const actorLabel = buildActorLabel(opts.performedBy);
    return updateOrderStatus(firestoreId, ORDER_STATUS.APPROVED, {
        ...opts,
        note: actorLabel ? `Approved by: ${actorLabel}` : 'Approved by admin',
    });
}

// ─── syncPaymongoPayment ──────────────────────────────────────────────────────
/** Re-check PayMongo for a paid status without creating a new charge (poor-connection recovery). */
export async function syncPaymongoPayment(firestoreId) {
    if (!firestoreId) return { paid: false, error: 'No order ID provided.', receiptNumber: null };
    try {
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('./config');
        if (!functions) return { paid: false, error: 'Payment service unavailable.', receiptNumber: null };
        const sync = httpsCallable(functions, 'syncPaymongoPayment');
        const { data } = await sync({ orderFirestoreId: firestoreId });
        return {
            paid: Boolean(data?.paid),
            error: null,
            receiptNumber: data?.receiptNumber || null,
        };
    } catch (err) {
        console.error('Error syncing PayMongo payment:', err);
        return { paid: false, error: getErrorMessage(err, 'Failed to sync payment status. Please try again.'), receiptNumber: null };
    }
}

// ─── cancelExpiredOrders ─────────────────────────────────────────────────────
export async function cancelExpiredOrders() {
    try {
        const q        = query(collection(db, 'orders'), where('status', '==', ORDER_STATUS.APPROVED));
        const snapshot = await getDocs(q);
        const now      = Date.now();
        const promises = [];

        snapshot.docs.forEach(d => {
            const data      = d.data();
            const expiresAt = data.expiresAt;
            if (!expiresAt) return;
            const expiryMs = new Date(expiresAt).getTime();
            if (now > expiryMs) {
                promises.push(
                    updateOrderStatus(d.id, ORDER_STATUS.CANCELLED, {
                        userId:             data.userId        || '',
                        customerName:       data.customerName  || '',
                        customerEmail:      data.customerEmail || '',
                        orderId:            data.orderId       || '',
                        orderTotal:         data.total         || 0,
                        cancellationReason: 'Approval window expired — customer did not check out within 72 hours.',
                        note:               'Auto-cancelled: approval window expired',
                    })
                );
            }
        });

        await Promise.all(promises);
        return { cancelled: promises.length, error: null };
    } catch (err) {
        console.error('Error cancelling expired orders:', err);
        return { cancelled: 0, error: getErrorMessage(err, 'Failed to cancel expired orders. Please try again.') };
    }
}

// ─── deleteOrder ─────────────────────────────────────────────────────────────
export async function deleteOrder(firestoreId) {
    if (!firestoreId) return { success: false, error: 'No order ID provided.' };
    try {
        await deleteDoc(doc(db, 'orders', firestoreId));
        return { success: true, error: null };
    } catch (err) {
        console.error('Error deleting order:', err);
        return { success: false, error: getErrorMessage(err, 'Failed to delete order. Please try again.') };
    }
}

// ─── archiveOrder ─────────────────────────────────────────────────────────────
export async function archiveOrder(firestoreId) {
    if (!firestoreId) return { success: false, error: 'No order ID provided.' };
    try {
        const orderRef  = doc(db, 'orders', firestoreId);
        const orderSnap = await getDoc(orderRef);
        if (!orderSnap.exists()) return { success: false, error: 'Order not found.' };

        await addDoc(collection(db, 'archivedOrders'), {
            ...orderSnap.data(),
            archivedAt: serverTimestamp(),
        });
        await deleteDoc(orderRef);
        return { success: true, error: null };
    } catch (err) {
        console.error('Error archiving order:', err);
        return { success: false, error: getErrorMessage(err, 'Failed to archive order. Please try again.') };
    }
}

// ─── getUserNotifications ─────────────────────────────────────────────────────
export async function getUserNotifications(userId) {
    if (!userId) return { notifications: [], error: 'No user ID.' };
    try {
        const q        = query(collection(db, 'notifications', userId, 'items'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const notifications = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        return { notifications, error: null };
    } catch (err) {
        return { notifications: [], error: getErrorMessage(err, 'Failed to fetch notifications. Please try again.') };
    }
}

// ─── markNotificationRead ─────────────────────────────────────────────────────
export async function markNotificationRead(userId, notificationId) {
    if (!userId || !notificationId) return;
    try {
        await updateDoc(doc(db, 'notifications', userId, 'items', notificationId), { read: true });
    } catch (err) {
        console.error('Error marking notification read:', err);
    }
}